import React, { useState } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { VoiceRecorder } from '@/components/transport/VoiceRecorder';
import { transportApi, getGPS } from '@/services/transportApi';

/** Driver rejects a stop that was added to his running trip — voice and/or typed reason. */
export const RejectStopDialog = ({ target, onClose, onDone }) => {
  const [audio, setAudio] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!audio && !text.trim()) { toast.error('Record a voice reason or type a reason'); return; }
    setBusy(true);
    try {
      const gps = await getGPS();
      const res = await transportApi.rejectLiveStop(target.trip.id, target.stop.stop_id, { reason_text: text, audio_base64: audio, gps });
      toast.success(res.transcript ? `Stop rejected — "${res.transcript.slice(0, 60)}"` : 'Stop rejected — dispatch notified');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reject-stop-dialog">
        <DialogHeader>
          <DialogTitle>Reject added stop</DialogTitle>
          <DialogDescription>{target?.stop?.kind === 'pickup' ? '▲ Pickup' : '▼ Drop'} · {target?.stop?.label} — added by {target?.stop?.added_by_name || 'staff'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <VoiceRecorder onChange={setAudio} testId="reject-voice" />
          <div className="space-y-1">
            <Label>Or type the reason</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="e.g. Vehicle is full / no time before shift end" data-testid="reject-reason-input" />
          </div>
          <p className="text-[11px] text-gray-500">Voice notes are converted to text automatically (Tamil or English) for the dispatcher.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reject-stop-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="reject-stop-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}Reject stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RejectStopDialog;
